import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import TaskCard from './TaskCard';

const COLUMNS = [
  { id: 'not_started', title: 'À faire', color: '#757575' },
  { id: 'in_progress', title: 'En cours', color: '#2196f3' },
  { id: 'completed', title: 'Terminé', color: '#4caf50' },
  { id: 'blocked', title: 'Bloqué', color: '#f44336' }
];

function KanbanBoard({ tasks, onCreateTask, onEditTask, onDeleteTask, onStatusChange }) {
  const [columns, setColumns] = useState({});

  useEffect(() => {
    // Organiser les tâches par statut
    const organized = {
      not_started: tasks.filter(t => t.status === 'not_started'),
      in_progress: tasks.filter(t => t.status === 'in_progress'),
      completed: tasks.filter(t => t.status === 'completed'),
      blocked: tasks.filter(t => t.status === 'blocked')
    };
    setColumns(organized);
  }, [tasks]);

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;

    // Abandon si pas de destination
    if (!destination) {
      return;
    }

    // Abandon si même position
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const sourceColumn = columns[source.droppableId];
    const destColumn = columns[destination.droppableId];
    const task = sourceColumn.find(t => t.id.toString() === draggableId);

    // Même colonne : réorganisation
    if (source.droppableId === destination.droppableId) {
      const newTasks = Array.from(sourceColumn);
      const [removed] = newTasks.splice(source.index, 1);
      newTasks.splice(destination.index, 0, removed);

      setColumns({
        ...columns,
        [source.droppableId]: newTasks
      });

      // TODO: Appeler l'API pour mettre à jour l'ordre
    } else {
      // Déplacement vers une autre colonne : changement de statut
      const sourceTasks = Array.from(sourceColumn);
      const destTasks = Array.from(destColumn);
      const [removed] = sourceTasks.splice(source.index, 1);

      // Mettre à jour le statut de la tâche
      removed.status = destination.droppableId;
      destTasks.splice(destination.index, 0, removed);

      setColumns({
        ...columns,
        [source.droppableId]: sourceTasks,
        [destination.droppableId]: destTasks
      });

      // Appeler l'API pour mettre à jour le statut
      if (onStatusChange && task) {
        onStatusChange(task, destination.droppableId);
      }
    }
  };

  return (
    <Box>
      <DragDropContext onDragEnd={onDragEnd}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 2
          }}
        >
          {COLUMNS.map((column) => (
            <Droppable key={column.id} droppableId={column.id}>
              {(provided, snapshot) => (
                <Paper
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  sx={{
                    flex: '1 1 300px',
                    minWidth: 300,
                    maxWidth: 400,
                    bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'background.paper',
                    transition: 'background-color 0.2s'
                  }}
                  elevation={1}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: `3px solid ${column.color}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                        {column.title}
                      </Typography>
                      <Chip
                        label={columns[column.id]?.length || 0}
                        size="small"
                        sx={{ bgcolor: column.color, color: 'white' }}
                      />
                    </Box>
                    {column.id === 'not_started' && (
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={onCreateTask}
                      >
                        Ajouter
                      </Button>
                    )}
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      minHeight: 400,
                      maxHeight: 'calc(100vh - 300px)',
                      overflowY: 'auto'
                    }}
                  >
                    {columns[column.id]?.map((task, index) => (
                      <Draggable
                        key={task.id.toString()}
                        draggableId={task.id.toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            sx={{
                              mb: 2,
                              opacity: snapshot.isDragging ? 0.8 : 1,
                              transform: snapshot.isDragging ? 'rotate(2deg)' : 'none'
                            }}
                          >
                            <TaskCard
                              task={task}
                              onEdit={onEditTask}
                              onDelete={onDeleteTask}
                              onStatusChange={onStatusChange}
                            />
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}

                    {(!columns[column.id] || columns[column.id].length === 0) && (
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 4,
                          color: 'text.secondary',
                          fontSize: '0.875rem'
                        }}
                      >
                        Aucune tâche
                      </Box>
                    )}
                  </Box>
                </Paper>
              )}
            </Droppable>
          ))}
        </Box>
      </DragDropContext>
    </Box>
  );
}

export default KanbanBoard;
